// ═══════════════════════════════════════════════════════════════════════════════
// Forgot password (public)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { emailSchema } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { AppButton } from '../../components/ui/AppButton';

export const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');

      const parsed = emailSchema.safeParse(email.trim());
      if (!parsed.success) {
        setError(parsed.error.issues.map((i) => i.message).join(', '));
        return;
      }

      setIsSubmitting(true);
      try {
        const response = await api.post<{ message?: string }>('/auth/forgot-password', {
          email: parsed.data,
        });
        if (!response.success) {
          throw new Error(response.error ?? t('auth.errorGeneric'));
        }
        setSubmitted(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('auth.errorGeneric'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, t]
  );

  return (
    <AuthLayout
      onBackClick={() => navigate(-1)}
      footer={
        <Link className="font-medium text-primary-600 underline" to="/login">
          {t('auth.linkLogin')}
        </Link>
      }
    >
      <h1 className="text-2xl font-bold text-gray-900" id="forgot-heading">
        {t('auth.forgotTitle')}
      </h1>
      <p className="mt-1 text-sm text-gray-500">{t('auth.forgotSubtitle')}</p>

      {submitted ? (
        <p
          className="mt-6 rounded-lg bg-green-50 px-3 py-3 text-sm text-green-800"
          role="status"
        >
          {t('auth.forgotSuccess')}
        </p>
      ) : (
        <form
          className="mt-6 space-y-4"
          onSubmit={handleSubmit}
          aria-labelledby="forgot-heading"
          noValidate
        >
          {error ? (
            <div
              id="forgot-error"
              className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          <div>
            <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-700">
              {t('auth.emailLabel')}
            </label>
            <input
              id="forgot-email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:outline-none"
              aria-invalid={Boolean(error)}
              aria-describedby={error ? 'forgot-error' : undefined}
            />
          </div>

          <AppButton type="submit" variant="primary" fullWidth disabled={isSubmitting} className="shadow-sm">
            {isSubmitting ? t('auth.forgotSending') : t('auth.forgotSubmit')}
          </AppButton>
        </form>
      )}
    </AuthLayout>
  );
};
